import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "../../components/auth/auth.selector";
import HelmetWrapper from "../../HelmetWrapper";
import { Button, Col, Container, Row, Card, Badge, ProgressBar, Modal } from "react-bootstrap";
import ReactVortoxSpinner from "../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../components/payment-link-system/PaymentLinkTable";
import CommonStructured from "../../components/common-components/CommonStructured";
import SelectDropdownLabel from "../../components/components-react/SelectDropdownLabel";
import useGymsData from "../../hooks/useGymsData";
import { fetchTvMonitoringDashboardData, fetchTvMonitoringGymStatsData } from "../../redux/reducers/apiSlice";
import { toast } from "react-toastify";
import "./tv-monitoring.css";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Online", value: "online" },
  { label: "Offline", value: "offline" },
];

const STORAGE_HEALTH_OPTIONS = [
  { label: "All", value: "" },
  { label: "Healthy", value: "healthy" },
  { label: "Warning", value: "warning" },
  { label: "Critical", value: "critical" },
];

const TvMonitoringDashboard = () => {
  const dispatch = useDispatch();
  const authData = useSelector(selectAuth);
  const userInfoData = authData.user;
  const token = userInfoData?.token;

  const { tvMonitoringDashboard, tvMonitoringDashboardLoading, tvMonitoringGymStats } =
    useSelector((state) => state.api);

  const [selectedGym, setSelectedGym] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [selectedTv, setSelectedTv] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const { data: gymsData, isLoading: isLoadingGyms } = useGymsData();

  // Fetch dashboard data
  const fetchDashboardData = () => {
    const filters = {
      page: isPage + 1,
      limit: pageLimit,
      gym_id: selectedGym?.value || "",
      status_filter: statusFilter?.value || "",
    };
    dispatch(fetchTvMonitoringDashboardData({ filters, token }));
  };

  // Fetch gym stats
  const fetchGymStats = () => {
    dispatch(fetchTvMonitoringGymStatsData({ token }));
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchGymStats();
    }
  }, [token, selectedGym, statusFilter, isPage, pageLimit]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (token) {
        fetchDashboardData();
      }
    }, 300000);
    return () => clearInterval(interval);
  }, [token, selectedGym, statusFilter, isPage, pageLimit]);

  const handleReset = () => {
    setSelectedGym(null);
    setStatusFilter(null);
    setIsPage(0);
  };

  const handleViewDetails = (tv) => {
    setSelectedTv(tv);
    setShowDetailsModal(true);
  };

  // Summary stats
  const summary = tvMonitoringDashboard?.summary || {
    total_tvs: 0,
    online_count: 0,
    offline_count: 0,
    storage_warning_count: 0,
    storage_critical_count: 0,
  };

  const tvList = tvMonitoringDashboard?.tvs || [];

  // Format uptime
  const formatUptime = (seconds) => {
    if (!seconds) return "N/A";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Format storage
  const formatStorage = (mb) => {
    if (!mb && mb !== 0) return "N/A";
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${Math.round(mb)} MB`;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online": return "#28a745";
      case "offline": return "#dc3545";
      default: return "#6c757d";
    }
  };

  // Get health badge
  const getHealthBadge = (health) => {
    switch (health?.toLowerCase()) {
      case "healthy": return <Badge bg="success">Healthy</Badge>;
      case "warning": return <Badge bg="warning" text="dark">Warning</Badge>;
      case "critical": return <Badge bg="danger">Critical</Badge>;
      default: return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const tbColumns = [
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (cell, row) => (
        <div className="tv-gym-cell">
          <span className="gym-name">{row?.gym_name || "N/A"}</span>
          <span className="gym-city text-muted">{row?.gym_city || ""}</span>
        </div>
      ),
    },
    {
      dataField: "tv_name",
      text: "TV Name",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.tv_name || "N/A"}</span>
      ),
    },
     {
      dataField: "user_name",
      text: "User Details",
      formatter: (cell, row) => {
        return (
          <div className="flex-k flex-col-k items-center-k fs-small-k userGap">
            <span className="tb-data minWidth100 bold-text">
              {row?.user_name || "NA"}
            </span>
            <span className="lowercase underline">
              {row?.user_email || "NA"} <br /> {row?.user_mobile || "NA"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "live_status",
      text: "Status",
      formatter: (cell, row) => (
        <div className="status-cell">
          <span
            className="status-dot"
            style={{ backgroundColor: getStatusColor(row?.live_status) }}
          />
          <span style={{ color: getStatusColor(row?.live_status), fontWeight: 500 }}>
            {row?.live_status?.toUpperCase() || "UNKNOWN"}
          </span>
        </div>
      ),
    },
    {
      dataField: "storage_health",
      text: "Storage Health",
      formatter: (cell, row) => getHealthBadge(row?.storage_health),
    },
    {
      dataField: "storage_percent_used",
      text: "Storage Used",
      formatter: (cell, row) => {
        const percent = row?.storage_percent_used || 0;
        let variant = "success";
        if (percent >= 90) variant = "danger";
        else if (percent >= 75) variant = "warning";
        return (
          <div className="storage-progress">
            <ProgressBar
              now={percent}
              variant={variant}
              style={{ height: "8px", minWidth: "80px" }}
            />
            <span className="storage-percent">{percent}%</span>
          </div>
        );
      },
    },
    {
      dataField: "usb_free_mb",
      text: "USB Free",
      formatter: (cell, row) => {
        const free = row?.usb_free_mb;
        const total = row?.usb_total_mb;
        if (!total) return <span className="text-muted">No USB</span>;
        return (
          <div className="storage-info">
            <span className="free-space">{formatStorage(free)}</span>
            <span className="total-space text-muted">/ {formatStorage(total)}</span>
          </div>
        );
      },
    },
    {
      dataField: "total_videos",
      text: "Content",
      formatter: (cell, row) => (
        <div className="content-count">
          <span>{row?.total_videos || 0} videos</span>
          <span className="text-muted">{row?.total_images || 0} images</span>
        </div>
      ),
    },
    {
      dataField: "app_version",
      text: "App",
      formatter: (cell, row) => (
        <span className="app-version">v{row?.app_version || "?"}</span>
      ),
    },
    {
      dataField: "uptime_seconds",
      text: "Uptime",
      formatter: (cell, row) => (
        <span className="uptime">{formatUptime(row?.uptime_seconds)}</span>
      ),
    },
    {
      dataField: "action",
      text: "Actions",
      formatter: (cell, row) => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleViewDetails(row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <HelmetWrapper title="WTF: TV Monitoring Dashboard">
      <Container className="bg-cloud-k px-50px" fluid>
        <Row>
          <Col>
            <CommonStructured
              pageHeading="TV Monitoring Dashboard"
              isReload={fetchDashboardData}
            />
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4 g-3">
          <Col md={2}>
            <Card className="summary-card total">
              <Card.Body>
                <div className="summary-value">{summary.total_tvs}</div>
                <div className="summary-label">Total TVs</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="summary-card online">
              <Card.Body>
                <div className="summary-value text-success">{summary.online_count}</div>
                <div className="summary-label">Online</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="summary-card offline">
              <Card.Body>
                <div className="summary-value text-danger">{summary.offline_count}</div>
                <div className="summary-label">Offline</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card warning">
              <Card.Body>
                <div className="summary-value text-warning">{summary.storage_warning_count}</div>
                <div className="summary-label">Storage Warning</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card critical">
              <Card.Body>
                <div className="summary-value text-danger">{summary.storage_critical_count}</div>
                <div className="summary-label">Storage Critical</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <div className="cmnFilterStructureContainer ptTrackContainer">
          <div className="filterblock gridSpanTwo">
            <div className="filterHeadingNBtn">
              <h5 className="filterHeading">Filters</h5>
              <Button variant="outline-danger" onClick={handleReset}>
                Reset
              </Button>
            </div>
            <div className="filterblock grid__three">
              <SelectDropdownLabel
                options={gymsData?.length > 0 ? gymsData : []}
                value={selectedGym}
                setSelectedOption={setSelectedGym}
                name="Select Gym"
              />
              <SelectDropdownLabel
                options={STATUS_OPTIONS}
                value={statusFilter}
                setSelectedOption={setStatusFilter}
                name="Status"
              />
            </div>
          </div>
        </div>

        {/* TV Table */}
        <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt-3">
          {tvMonitoringDashboardLoading ? (
            <ReactVortoxSpinner />
          ) : (
            <PaymentLinkTable
              tableData={tvList}
              tableColumns={tbColumns}
              tablePagination={tvMonitoringDashboard?.pagination}
              isPage={isPage}
              setIsPage={(value) => setIsPage(value)}
              setPageLimit={setPageLimit}
              pageLimit={pageLimit}
            />
          )}
        </div>

        {/* Details Modal */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>TV Details - {selectedTv?.tv_name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTv && (
              <div className="tv-details-grid">
                <div className="detail-section">
                  <h6>Basic Info</h6>
                  <div className="detail-row">
                    <span className="detail-label">Gym:</span>
                    <span className="detail-value">{selectedTv.gym_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value" style={{ color: getStatusColor(selectedTv.live_status) }}>
                      {selectedTv.live_status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">App Version:</span>
                    <span className="detail-value">v{selectedTv.app_version} (Build {selectedTv.app_build})</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Device:</span>
                    <span className="detail-value">{selectedTv.device_model || "N/A"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Android:</span>
                    <span className="detail-value">{selectedTv.android_version || "N/A"}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h6>Storage</h6>
                  <div className="detail-row">
                    <span className="detail-label">Mode:</span>
                    <span className="detail-value">{selectedTv.storage_mode?.toUpperCase() || "N/A"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Health:</span>
                    <span className="detail-value">{getHealthBadge(selectedTv.storage_health)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">USB Total:</span>
                    <span className="detail-value">{formatStorage(selectedTv.usb_total_mb)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">USB Free:</span>
                    <span className="detail-value">{formatStorage(selectedTv.usb_free_mb)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Used:</span>
                    <span className="detail-value">{selectedTv.storage_percent_used || 0}%</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h6>Content</h6>
                  <div className="detail-row">
                    <span className="detail-label">Videos:</span>
                    <span className="detail-value">{selectedTv.total_videos || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Images:</span>
                    <span className="detail-value">{selectedTv.total_images || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Content Size:</span>
                    <span className="detail-value">{formatStorage(selectedTv.total_content_size_mb)}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h6>Performance</h6>
                  <div className="detail-row">
                    <span className="detail-label">Uptime:</span>
                    <span className="detail-value">{formatUptime(selectedTv.uptime_seconds)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Memory:</span>
                    <span className="detail-value">{selectedTv.memory_percent || 0}%</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Network:</span>
                    <span className="detail-value">{selectedTv.network_type || "N/A"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">WiFi:</span>
                    <span className="detail-value">{selectedTv.wifi_ssid || "N/A"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">IP:</span>
                    <span className="detail-value">{selectedTv.ip_address || "N/A"}</span>
                  </div>
                </div>

                {selectedTv.usb_devices && selectedTv.usb_devices.length > 0 && (
                  <div className="detail-section full-width">
                    <h6>USB Devices</h6>
                    <div className="usb-devices-list">
                      {selectedTv.usb_devices.map((device, idx) => (
                        <div key={idx} className="usb-device-card">
                          <div className="usb-label">{device.label || `USB ${idx + 1}`}</div>
                          <div className="usb-info">
                            {formatStorage(device.free_mb)} free / {formatStorage(device.total_mb)} total
                          </div>
                          <div className="usb-fs">{device.filesystem}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </HelmetWrapper>
  );
};

export default TvMonitoringDashboard;